/**
 * Modal that shows a calendar-style breakdown of leave days:
 * matches the exact 2-pane interactive layout of the leave date picker modal.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { toDateKey, publicHolidayDateSet, getDaysBreakdown } from '../utils/workingDays';
import './DaysTakenBreakdownModal.css';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function nextPortion(current) {
  if (current === 'full') return 'am';
  if (current === 'am') return 'pm';
  return 'full';
}

function formatLongDate(d) {
  return typeof d === 'string'
    ? new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })
    : d.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
}

function formatShortDay(dateKey) {
  if (!dateKey) return '';
  const d = new Date(`${dateKey}T12:00:00`);
  if (Number.isNaN(d.getTime())) return dateKey;
  return d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
}

function DaysTakenBreakdownModal({
  show,
  onClose,
  startDate,
  endDate,
  publicHolidays = [],
  daysTaken = 0,
  employeeWorkingSettings = null,
  overrideWindows = null,
  halfDay = null,
  halfDayOn = null,
  halfDays = null,
  editable = false,
  onPortionChange = null,
}) {
  const initialStart = useMemo(() => {
    if (!startDate) return new Date();
    const d = new Date(startDate);
    return Number.isNaN(d.getTime()) ? new Date() : d;
  }, [startDate]);

  const [viewYear, setViewYear] = useState(initialStart.getFullYear());
  const [viewMonth, setViewMonth] = useState(initialStart.getMonth());

  useEffect(() => {
    if (!startDate) return;
    const d = new Date(startDate);
    if (!Number.isNaN(d.getTime())) {
      setViewYear(d.getFullYear());
      setViewMonth(d.getMonth());
    }
  }, [startDate, show]);

  const { dayMapByKey, startKey, endKey, resolvedHalfMap } = useMemo(() => {
    if (!startDate || !endDate) {
      return { dayMapByKey: {}, startKey: null, endKey: null, resolvedHalfMap: {} };
    }
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) {
      return { dayMapByKey: {}, startKey: null, endKey: null, resolvedHalfMap: {} };
    }

    const phSet = publicHolidayDateSet(publicHolidays);
    const nameMap = new Map();
    (publicHolidays || []).forEach((h) => {
      if (typeof h !== 'object' || h == null || h.date == null) return;
      const startH = new Date(h.date);
      startH.setHours(0, 0, 0, 0);
      const endH = h.endDate ? new Date(h.endDate) : new Date(h.date);
      endH.setHours(0, 0, 0, 0);
      const label = h.name || h.nameFr || 'Public holiday';
      const d = new Date(startH);
      while (d <= endH) {
        nameMap.set(toDateKey(d), label);
        d.setDate(d.getDate() + 1);
      }
    });

    const breakdown = getDaysBreakdown(start, end, phSet, nameMap, employeeWorkingSettings, overrideWindows);
    const sKey = toDateKey(start);
    const eKey = toDateKey(end);
    const halfMap = (halfDays && typeof halfDays === 'object' && !Array.isArray(halfDays)) ? { ...halfDays } : {};
    const edgeKey = (halfDay === 'am' || halfDay === 'pm')
      ? (sKey === eKey ? sKey : halfDayOn === 'start' ? sKey : halfDayOn === 'end' ? eKey : null)
      : null;
    if (edgeKey && !halfMap[edgeKey]) {
      halfMap[edgeKey] = halfDay;
    }

    const mapByKey = {};
    breakdown.forEach((item) => {
      const part = halfMap[item.dateKey];
      if ((part === 'am' || part === 'pm') && item.type === 'counted') {
        item.type = 'requested_half';
        item.portion = part;
        item.label = part === 'am' ? 'Morning off' : 'Afternoon off';
      }
      mapByKey[item.dateKey] = item;
    });

    return { dayMapByKey: mapByKey, startKey: sKey, endKey: eKey, resolvedHalfMap: halfMap };
  }, [startDate, endDate, publicHolidays, employeeWorkingSettings, overrideWindows, halfDay, halfDayOn, halfDays]);

  if (!show) return null;

  const start = startDate ? new Date(startDate) : null;
  const end = endDate ? new Date(endDate) : null;
  const startLabel = start && !Number.isNaN(start.getTime()) ? formatLongDate(start) : '–';
  const endLabel = end && !Number.isNaN(end.getTime()) ? formatLongDate(end) : '–';
  const rangeLabel = start && end && !Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())
    ? `${formatLongDate(start)} – ${formatLongDate(end)}`
    : '';
  const halfEntries = Object.entries(resolvedHalfMap).filter(([, p]) => p === 'am' || p === 'pm').sort(([a], [b]) => a.localeCompare(b));

  const firstDay = new Date(viewYear, viewMonth, 1);
  const lastDay = new Date(viewYear, viewMonth + 1, 0);
  const startOfWeek = firstDay.getDay();
  const daysInMonth = lastDay.getDate();
  const monthName = firstDay.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });

  const handlePrevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear((y) => y - 1);
    } else {
      setViewMonth((m) => m - 1);
    }
  };

  const handleNextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear((y) => y + 1);
    } else {
      setViewMonth((m) => m + 1);
    }
  };

  return (
    <div
      className="modal fade show d-block"
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-labelledby="daysBreakdownModalTitle"
      style={{ backgroundColor: 'rgba(15, 23, 42, 0.45)', zIndex: 1055 }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="modal-dialog modal-dialog-centered"
        style={{ maxWidth: 740 }}
        role="document"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content border-0 shadow-lg leave-picker-modal-content">
          <div className="modal-header border-bottom px-4 py-3">
            <h6 className="modal-title fw-semibold mb-0" id="daysBreakdownModalTitle">
              <i className="bi bi-calendar3 me-2 text-success" />
              Leave days breakdown
            </h6>
            <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
          </div>

          <div className="modal-body p-0">
            <p className="text-muted visually-hidden">
              Leave range: <strong>{rangeLabel}</strong>
            </p>
            <div className="row g-0">
              <div className="col-12 col-md-5 p-4 d-flex flex-column justify-content-between leave-picker-left-pane">
                <div>
                  <div className="mb-3">
                    <div className="small fw-medium text-secondary mb-1">Start date</div>
                    <div className="px-3 py-2 rounded-3 bg-white border fw-medium text-dark small">
                      {startLabel}
                    </div>
                  </div>
                  <div className="mb-3">
                    <div className="small fw-medium text-secondary mb-1">End date</div>
                    <div className="px-3 py-2 rounded-3 bg-white border fw-medium text-dark small">
                      {endLabel}
                    </div>
                  </div>

                  <div className="days-breakdown-summary p-3 rounded-3 bg-white border mb-3">
                    <div className="small text-secondary mb-1">Total leave</div>
                    <div className="fs-4 fw-bold text-success">
                      {daysTaken} {daysTaken === 1 ? 'day' : 'days'} counted
                    </div>
                    {halfEntries.length > 0 && (
                      <div className="mt-2 pt-2 border-top d-flex flex-column gap-1">
                        {halfEntries.map(([k, part]) => (
                          <div key={k} className="small text-dark">
                            {formatShortDay(k)}: <strong>{part === 'am' ? 'Morning off' : 'Afternoon off'}</strong>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="d-flex flex-wrap gap-3 small text-secondary pt-2">
                  <span><span className="days-breakdown-legend days-breakdown-counted" />Full day</span>
                  <span>
                    <span className="days-breakdown-legend days-breakdown-half" />
                    Half day (0.5d)
                    {employeeWorkingSettings != null && (
                      <span className="visually-hidden"> / Half day (your schedule)</span>
                    )}
                  </span>
                  <span>
                    <span className="days-breakdown-legend days-breakdown-weekend" />
                    Off / Holiday
                    <span className="visually-hidden"> Weekend</span>
                  </span>
                </div>
              </div>

              <div className="col-12 col-md-7 p-4 bg-white d-flex flex-column justify-content-between">
                <div>
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <button
                      type="button"
                      className="btn btn-sm btn-light border px-2 py-1"
                      aria-label="Previous month"
                      onClick={handlePrevMonth}
                    >
                      <i className="bi bi-chevron-left" />
                    </button>
                    <span className="fw-semibold">{monthName}</span>
                    <button
                      type="button"
                      className="btn btn-sm btn-light border px-2 py-1"
                      aria-label="Next month"
                      onClick={handleNextMonth}
                    >
                      <i className="bi bi-chevron-right" />
                    </button>
                  </div>

                  <div className="days-breakdown-picker-grid">
                    {WEEKDAYS.map((wd) => (
                      <div key={wd} className="days-breakdown-cell days-breakdown-header">{wd}</div>
                    ))}
                    {Array.from({ length: startOfWeek }, (_, i) => (
                      <div key={`empty-${i}`} className="days-breakdown-cell days-breakdown-empty" />
                    ))}
                    {Array.from({ length: daysInMonth }, (_, i) => {
                      const dayNum = i + 1;
                      const dateObj = new Date(viewYear, viewMonth, dayNum, 12, 0, 0);
                      const dateKey = toDateKey(dateObj);
                      const info = dayMapByKey[dateKey];
                      const isWeekendDay = dateObj.getDay() === 0 || dateObj.getDay() === 6;
                      const edge = info && startKey && endKey && (info.dateKey === startKey || info.dateKey === endKey)
                        ? (startKey === endKey || info.dateKey === startKey ? 'start' : 'end')
                        : null;
                      const canEdit = Boolean(
                        editable
                        && onPortionChange
                        && edge
                        && info
                        && (info.type === 'counted' || info.type === 'requested_half')
                      );
                      let cellClass = 'days-breakdown-cell ';
                      let pillTag = null;
                      if (info) {
                        if (info.type === 'counted') {
                          cellClass += 'days-breakdown-counted';
                        } else if (info.type === 'half') {
                          cellClass += 'days-breakdown-half';
                          pillTag = '0.5d';
                        } else if (info.type === 'requested_half') {
                          cellClass += 'days-breakdown-half days-breakdown-requested ';
                          cellClass += info.portion === 'pm'
                            ? 'days-breakdown-requested-pm'
                            : 'days-breakdown-requested-am';
                          pillTag = info.portion === 'pm' ? 'PM off' : 'AM off';
                        } else if (info.type === 'weekend') {
                          cellClass += 'days-breakdown-weekend';
                        } else if (info.type === 'non_working') {
                          cellClass += 'days-breakdown-nonworking';
                        } else {
                          cellClass += 'days-breakdown-holiday';
                        }
                      } else if (isWeekendDay) {
                        cellClass += 'days-breakdown-out text-muted';
                      } else {
                        cellClass += 'days-breakdown-out text-dark';
                      }
                      const title = info
                        ? (canEdit ? `${info.label || 'All day'}. Click to change.` : (info.label || info.type))
                        : '';
                      if (canEdit) {
                        const current = info.type === 'requested_half' ? info.portion : 'full';
                        return (
                          <button
                            key={dayNum}
                            type="button"
                            className={cellClass}
                            title={title}
                            aria-label={`${dayNum}, ${current === 'full' ? 'all day' : current === 'am' ? 'morning only' : 'afternoon only'}`}
                            onClick={() => onPortionChange(edge, nextPortion(current))}
                          >
                            <span style={{ lineHeight: 1.1 }}>{dayNum}</span>
                            {pillTag && <span className="days-breakdown-pill-tag">{pillTag}</span>}
                          </button>
                        );
                      }
                      return (
                        <div key={dayNum} className={cellClass} title={title}>
                          <span style={{ lineHeight: 1.1 }}>{dayNum}</span>
                          {pillTag && <span className="days-breakdown-pill-tag">{pillTag}</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="modal-footer bg-light border-top px-4 py-2 d-flex justify-content-end">
            <button type="button" className="btn btn-primary btn-sm px-4" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DaysTakenBreakdownModal;
